import React from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Field, Form, Formik } from "formik";
import moment from "moment";
import * as Yup from "yup";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import PasswordInput from "components/Form/Inputs/PasswordInput";
import TextInput from "components/Form/Inputs/TextInput";
import authApi from "services/apis/authApi";
import { getPathPrefix } from "utils/coreUtils";
import ToastUtils from "utils/ToastUtils";
import StorageManager from "utils/storageManager";
import { OTP_SCREEN_TYPE } from "./AuthOTP";

const Login = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const companyDetails = useSelector((state) => state?.company);

  const handleLogin = async (values, actions) => {
    try {
      const res = await authApi.authenticate({
        password: values.password,
        username: values.email.trim(),
        companyId: companyDetails?.companyId,
      });

      StorageManager.set("otpSentTime", moment().toISOString());

      if (res.data.isLoginFirstTime) {
        navigate(getPathPrefix("new-user"), {
          state: { encUrl: "encyptedKey=" + res.data.encryptedKey },
        });
      } else if (res.data.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${res?.data?.code}`));
      } else {
        toast.success(t(`statusCodes.${res?.data?.code}`));
        navigate(getPathPrefix() + `otp`, {
          state: {
            type: OTP_SCREEN_TYPE.LOGIN,
            password: values.password,
            username: values.email.trim(),
          },
        });
      }
    } catch (error) {
      /* empty */
    }

    actions.setSubmitting(false);
  };

  const initialFormValues = {
    email: "",
    password: "",
  };

  const formValidationSchema = Yup.object({
    email: Yup.string().required(t("companyLogin.usernameOrEmailRequired")),
    password: Yup.string()
      .label("Password")
      .required(t("companyLogin.passwordRequired")),
  });

  return (
    <Formik
      initialValues={initialFormValues}
      validateOnMount
      className="mt-2"
      validationSchema={formValidationSchema}
      onSubmit={handleLogin}
    >
      {({ handleSubmit, isValid, isSubmitting, dirty }) => (
        <Form onSubmit={handleSubmit}>
          <div className="py-4">
            <h3
              className="text-primary-app mb-4 text-center"
              style={{ fontWeight: "600" }}
            >
              {t("companyLogin.loginTitle")}
            </h3>

            <div className="mb-2">
              <Field
                rightIcon={<i className="fa-solid fa-envelope"></i>}
                component={TextInput}
                label={t("companyLogin.usernameOrEmailLabel")}
                placeholder={t("companyLogin.usernameOrEmailHint")}
                id="email"
                name="email"
                autoComplete="off"
                required
              />
            </div>
            <div className="mb-2">
              <Field
                component={PasswordInput}
                label={t("companyLogin.passwordLabel")}
                placeholder={t("companyLogin.passwordHint")}
                id="password"
                name="password"
                autoComplete="password"
                rightIcon={<i className="fa-solid fa-lock"></i>}
                required
              />
            </div>
            <div className="text-start mt-2 ms-0">
              <NavLink to={getPathPrefix() + "forgot-password"} className="">
                <small>{t("companyLogin.forgotPasswordText")}</small>
              </NavLink>
            </div>
          </div>

          <div className="text-center mb-2">
            <PrimaryButton
              type="submit"
              title={t("CommonKeys.login_hyphen")}
              icon="fa-solid fa-right-to-bracket"
              disabled={!isValid || isSubmitting || !dirty}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Login;
